import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Clock, LogIn, LogOut } from 'lucide-react';
import { useRequestProcessor } from '../../requestProcessor';
import { useAtom } from 'jotai';
import { activeChildDetail } from '../../atomstore/store';
import { getAttendance } from '../../queryfunctions/queryfunctions';
import SpinnerLoading from './SpinnerLoading';
import { useNavigate } from 'react-router-dom';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

function getFiscalYearStart() {
    const now = new Date();
    const year = now.getMonth() > 2 ? now.getFullYear() : now.getFullYear() - 1;
    return { month: 3, year };
}

function formatTime(timeStr) {
    if (!timeStr) return '—';
    const sliced = timeStr.slice(0, 5);
    return sliced && sliced !== '00:00' ? sliced : '—';
}

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
    return new Date(year, month, 1).getDay();
}

const CheckInHistory = () => {
    const { useQueryWrapper } = useRequestProcessor();
    const [activeChild] = useAtom(activeChildDetail);
    const navigate = useNavigate();

    const now = new Date();
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());

    const {
        data: attendanceList = [],
        isLoading,
        isFetching,
    } = useQueryWrapper(
        ['getAttendance', 'attendance' + activeChild?.id],
        () => getAttendance()
    );

    const attendanceMap = useMemo(() => {
        const map = {};
        attendanceList.forEach((item) => {
            const d = new Date(item.date);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            map[key] = item;
        });
        return map;
    }, [attendanceList]);

    const monthData = useMemo(() => {
        return attendanceList
            .filter((item) => {
                const d = new Date(item.date);
                return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth;
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [attendanceList, selectedMonth, selectedYear]);

    const summary = useMemo(() => {
        let present = 0;
        let absent = 0;
        monthData.forEach((item) => {
            if (item.status?.toLowerCase() === 'present') present++;
            else if (item.status?.toLowerCase() === 'absent') absent++;
        });
        const total = present + absent;
        const percentage = total > 0 ? Math.round((present / total) * 100) : 0;
        return { present, absent, percentage };
    }, [monthData]);

    const fiscalStart = getFiscalYearStart();
    const canGoPrev = selectedYear > fiscalStart.year || (selectedYear === fiscalStart.year && selectedMonth > fiscalStart.month);
    const canGoNext = selectedYear < now.getFullYear() || (selectedYear === now.getFullYear() && selectedMonth < now.getMonth());

    const handlePrevMonth = () => {
        if (!canGoPrev) return;
        if (selectedMonth === 0) {
            setSelectedMonth(11);
            setSelectedYear((y) => y - 1);
        } else {
            setSelectedMonth((m) => m - 1);
        }
    };

    const handleNextMonth = () => {
        if (!canGoNext) return;
        if (selectedMonth === 11) {
            setSelectedMonth(0);
            setSelectedYear((y) => y + 1);
        } else {
            setSelectedMonth((m) => m + 1);
        }
    };

    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const todayDate = now.getDate();
    const todayMonth = now.getMonth();
    const todayYear = now.getFullYear();

    const calendarCells = useMemo(() => {
        const cells = [];
        for (let i = 0; i < firstDay; i++) {
            cells.push({ type: 'empty', key: `e-${i}` });
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const key = `${selectedYear}-${selectedMonth}-${day}`;
            const record = attendanceMap[key];
            const isToday = day === todayDate && selectedMonth === todayMonth && selectedYear === todayYear;
            cells.push({ type: 'day', day, record, isToday, key: `d-${day}` });
        }
        return cells;
    }, [selectedYear, selectedMonth, daysInMonth, firstDay, attendanceMap, todayDate, todayMonth, todayYear]);

    if (isLoading || isFetching) {
        return <SpinnerLoading />;
    }

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center space-x-3 z-10">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>
                    Check-in History
                </h2>
            </div>

            <div className="p-4 lg:flex lg:gap-6 lg:items-start">
                {/* Left Column — Calendar + Summary */}
                <div className="lg:w-5/12 lg:sticky lg:top-16 space-y-3">
                    {/* Calendar Section */}
                    <div className="bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl p-4 shadow-lg">
                        {/* Month Navigation */}
                        <div className="flex items-center justify-between mb-4">
                            <button
                                onClick={handlePrevMonth}
                                disabled={!canGoPrev}
                                className={`w-9 h-9 rounded-full flex items-center justify-center transition ${canGoPrev ? 'bg-white bg-opacity-20 active:bg-opacity-30' : 'opacity-30 cursor-not-allowed'}`}
                            >
                                <ChevronLeft className="w-5 h-5 text-white" />
                            </button>
                            <div className="text-center">
                                <p className="text-white text-lg font-bold">
                                    {MONTH_NAMES[selectedMonth]} {selectedYear}
                                </p>
                            </div>
                            <button
                                onClick={handleNextMonth}
                                disabled={!canGoNext}
                                className={`w-9 h-9 rounded-full flex items-center justify-center transition ${canGoNext ? 'bg-white bg-opacity-20 active:bg-opacity-30' : 'opacity-30 cursor-not-allowed'}`}
                            >
                                <ChevronRight className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        {/* Day Headers */}
                        <div className="grid grid-cols-7 gap-1 mb-2">
                            {DAY_LABELS.map((label) => (
                                <div key={label} className="text-center text-white text-xs font-medium opacity-70">
                                    {label}
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7 gap-1">
                            {calendarCells.map((cell) => {
                                if (cell.type === 'empty') {
                                    return <div key={cell.key} className="aspect-square" />;
                                }

                                const status = cell.record?.status?.toLowerCase();
                                const isPresent = status === 'present';
                                const isAbsent = status === 'absent';

                                let bgClass = 'bg-white bg-opacity-10';
                                if (isPresent) bgClass = 'bg-green-700 bg-opacity-90';
                                else if (isAbsent) bgClass = 'bg-red-700 bg-opacity-90';

                                return (
                                    <div
                                        key={cell.key}
                                        className={`aspect-square rounded-lg flex items-center justify-center text-sm font-semibold transition ${bgClass} ${cell.isToday ? 'ring-2 ring-white ring-offset-1 ring-offset-teal-400' : ''}`}
                                    >
                                        <span className={isPresent || isAbsent ? 'text-white' : 'text-white text-opacity-50'}>
                                            {cell.day}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Summary Bar */}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-green-600">{summary.present}</p>
                            <p className="text-xs text-green-600 font-medium">Present</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-red-600">{summary.absent}</p>
                            <p className="text-xs text-red-600 font-medium">Absent</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-blue-600">{summary.percentage}%</p>
                            <p className="text-xs text-blue-600 font-medium">Attendance</p>
                        </div>
                    </div>
                </div>

                {/* Right Column — Detail Table */}
                <div className="mt-4 lg:mt-0 lg:flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-700">Daily Attendance</h3>
                    </div>

                    {monthData.length > 0 ? (
                        <div className="divide-y divide-gray-50">
                            {monthData.map((item, idx) => {
                                const d = new Date(item.date);
                                const dayNum = d.getDate();
                                const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
                                const isAbsent = item.status?.toLowerCase() === 'absent';

                                return (
                                    <div key={idx} className="px-4 py-3 flex items-center">
                                        <div className="w-12 text-center mr-3">
                                            <p className="text-lg font-bold text-gray-800">{dayNum}</p>
                                            <p className="text-xs text-gray-400">{dayName}</p>
                                        </div>

                                        <div className="mr-auto">
                                            {isAbsent ? (
                                                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                                                    Absent
                                                </span>
                                            ) : (
                                                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                                                    Present
                                                </span>
                                            )}
                                        </div>

                                        {!isAbsent && (
                                            <div className="flex items-center space-x-4 text-sm">
                                                <div className="flex items-center space-x-1 text-gray-600">
                                                    <LogIn className="w-3.5 h-3.5 text-green-500" />
                                                    <span className="font-medium">{formatTime(item.time_in)}</span>
                                                </div>
                                                <div className="flex items-center space-x-1 text-gray-600">
                                                    <LogOut className="w-3.5 h-3.5 text-red-400" />
                                                    <span className="font-medium">{formatTime(item.time_out)}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-10 text-center">
                            <p className="text-gray-400 text-sm">No records for this month</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CheckInHistory;
